<template>
  <div
    ref="root"
    class="base-scroll-shadow"
    :class="rootClasses"
    :style="rootStyle"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'

type ScrollShadowOrientation = 'horizontal' | 'vertical'

const props = withDefaults(defineProps<{
  orientation?: ScrollShadowOrientation,
  size?: string,
  color?: string,
  transparentColor?: string,
  mobileOnly?: boolean,
  scrollTarget?: string,
  autoScrollToEnd?: boolean
}>(), {
  orientation: 'horizontal',
  size: '3rem',
  color: '#F5F5F5',
  transparentColor: 'rgba(245, 245, 245, 0)',
  mobileOnly: false,
  scrollTarget: '',
  autoScrollToEnd: false
})

const root = ref<HTMLElement | null>(null)
const canScrollStart = shallowRef(false)
const canScrollEnd = shallowRef(false)
let scrollElement: HTMLElement | null = null
let resizeObserver: ResizeObserver | null = null
let mutationObserver: MutationObserver | null = null
let lastScrollSize: number | null = null
const resizeObservedElements = new Set<Element>()

const rootClasses = computed(() => [
  `base-scroll-shadow--${props.orientation}`,
  props.mobileOnly && 'base-scroll-shadow--mobile-only',
  canScrollStart.value && 'can-scroll-start',
  canScrollEnd.value && 'can-scroll-end'
])

const rootStyle = computed(() => [
  `--base-scroll-shadow-size: ${props.size}`,
  `--base-scroll-shadow-color: ${props.color}`,
  `--base-scroll-shadow-transparent-color: ${props.transparentColor}`
].join(';'))

const updateScrollState = () => {
  if (!scrollElement) {
    canScrollStart.value = false
    canScrollEnd.value = false
    return
  }

  const threshold = 1
  const position = props.orientation === 'horizontal' ? scrollElement.scrollLeft : scrollElement.scrollTop
  const scrollSize = props.orientation === 'horizontal' ? scrollElement.scrollWidth : scrollElement.scrollHeight
  const clientSize = props.orientation === 'horizontal' ? scrollElement.clientWidth : scrollElement.clientHeight
  const maxScrollPosition = scrollSize - clientSize

  canScrollStart.value = maxScrollPosition > threshold && position > threshold
  canScrollEnd.value = maxScrollPosition > threshold && position < maxScrollPosition - threshold
}

const autoScrollToEnd = () => {
  if (!scrollElement) return

  const threshold = 1
  const scrollSize = props.orientation === 'horizontal' ? scrollElement.scrollWidth : scrollElement.scrollHeight
  const clientSize = props.orientation === 'horizontal' ? scrollElement.clientWidth : scrollElement.clientHeight
  const maxScrollPosition = scrollSize - clientSize
  const scrollSizeIncreased = lastScrollSize === null || scrollSize > lastScrollSize

  lastScrollSize = scrollSize

  if (!props.autoScrollToEnd || !scrollSizeIncreased || maxScrollPosition <= threshold) return

  scrollElement.scrollTo(props.orientation === 'horizontal'
    ? { left: maxScrollPosition, behavior: 'smooth' }
    : { top: maxScrollPosition, behavior: 'smooth' })
}

const updateMeasuredScrollState = () => {
  updateScrollState()
  autoScrollToEnd()
}

const refreshResizeObservedElements = () => {
  if (!scrollElement || !resizeObserver) return

  const nextObservedElements = new Set<Element>([
    scrollElement,
    ...Array.from(scrollElement.querySelectorAll('*'))
  ])

  resizeObservedElements.forEach((element) => {
    if (nextObservedElements.has(element)) return
    resizeObserver?.unobserve(element)
    resizeObservedElements.delete(element)
  })

  nextObservedElements.forEach((element) => {
    if (resizeObservedElements.has(element)) return
    resizeObserver?.observe(element)
    resizeObservedElements.add(element)
  })
}

const disconnectScrollElement = () => {
  scrollElement?.removeEventListener('scroll', updateScrollState)
  resizeObserver?.disconnect()
  scrollElement = null
  resizeObserver = null
  lastScrollSize = null
  resizeObservedElements.clear()
}

const resolveScrollElement = () => {
  if (!root.value) return null
  if (props.scrollTarget) return root.value.querySelector<HTMLElement>(props.scrollTarget)
  return root.value.firstElementChild instanceof HTMLElement ? root.value.firstElementChild : null
}

const connectScrollElement = () => {
  const nextScrollElement = resolveScrollElement()
  if (nextScrollElement === scrollElement) {
    refreshResizeObservedElements()
    updateMeasuredScrollState()
    return
  }

  disconnectScrollElement()
  scrollElement = nextScrollElement
  if (!scrollElement) {
    updateScrollState()
    return
  }

  scrollElement.addEventListener('scroll', updateScrollState, { passive: true })
  resizeObserver = new ResizeObserver(scheduleScrollStateUpdate)
  refreshResizeObservedElements()
  updateMeasuredScrollState()
}

const scheduleScrollStateUpdate = () => {
  void nextTick(connectScrollElement)
}

watch([
  () => props.orientation,
  () => props.scrollTarget
], () => {
  lastScrollSize = null
  scheduleScrollStateUpdate()
})

watch(() => props.autoScrollToEnd, (enabled) => {
  if (!enabled) return
  lastScrollSize = null
  scheduleScrollStateUpdate()
})

onMounted(() => {
  mutationObserver = new MutationObserver(scheduleScrollStateUpdate)
  if (root.value) {
    mutationObserver.observe(root.value, {
      childList: true,
      subtree: true,
      characterData: true
    })
  }
  scheduleScrollStateUpdate()
})

onBeforeUnmount(() => {
  mutationObserver?.disconnect()
  disconnectScrollElement()
})
</script>

<style lang="scss" scoped>
.base-scroll-shadow {
  position: relative;
  min-width: 0;
  max-width: 100%;

  &::before,
  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    pointer-events: none;
    opacity: 0;
  }

  &--horizontal {
    &::before,
    &::after {
      top: 0;
      bottom: 0;
      width: var(--base-scroll-shadow-size);
    }

    &::before {
      left: 0;
      background: linear-gradient(270deg, var(--base-scroll-shadow-transparent-color) 0%, var(--base-scroll-shadow-color) 100%);
    }

    &::after {
      right: 0;
      background: linear-gradient(90deg, var(--base-scroll-shadow-transparent-color) 0%, var(--base-scroll-shadow-color) 100%);
    }
  }

  &--vertical {
    &::before,
    &::after {
      right: 0;
      left: 0;
      height: var(--base-scroll-shadow-size);
    }

    &::before {
      top: 0;
      background: linear-gradient(0deg, var(--base-scroll-shadow-transparent-color) 0%, var(--base-scroll-shadow-color) 100%);
    }

    &::after {
      bottom: 0;
      background: linear-gradient(180deg, var(--base-scroll-shadow-transparent-color) 0%, var(--base-scroll-shadow-color) 100%);
    }
  }

  &.can-scroll-start::before,
  &.can-scroll-end::after {
    opacity: 1;
  }

  @media screen and (min-width: 768px) {
    &--mobile-only::before,
    &--mobile-only::after {
      display: none;
    }
  }
}
</style>
